import axios, {
  AxiosInstance,
  AxiosError,
  InternalAxiosRequestConfig,
} from "axios";
import toast from "react-hot-toast";
import { API_BASE_URL, API_ENDPOINTS } from "../utils/constants";
import { ApiResponse } from "../types";
import { store } from "../store/store";
import { clearUser, updateUserContext } from "../store/slices/auth.slice";
import { resetLocationState, setLocationListHydrated } from "../store/slices/location.slice";
import { invalidateLocationListCache } from "./location.service";

export function getErrorMessage(error: AxiosError<ApiResponse>): string {
  const message = error.response?.data?.message;
  if (typeof message === "string" && message.trim()) return message;
  if (error.response?.status) {
    return `Request failed (${error.response.status})`;
  }
  return error.message || "Something went wrong";
}

function isCanceledError(error: AxiosError<ApiResponse>): boolean {
  return error.code === "ERR_CANCELED" || error.name === "CanceledError";
}

const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // Important for HTTP-only cookies
  headers: {
    "Content-Type": "application/json",
  },
});

let refreshPromise: Promise<unknown> | null = null;

function doRefresh(): Promise<unknown> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = api.post(API_ENDPOINTS.AUTH.REFRESH, undefined, {
    withCredentials: true,
  });
  refreshPromise.finally(() => {
    refreshPromise = null;
  });
  return refreshPromise;
}

// Request interceptor
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor: on 401 try refresh once, then retry or clear session.
// Also apply meta.user from authenticated responses so role/permission changes take effect without refresh.
api.interceptors.response.use(
  (response) => {
    const metaUser = response?.data?.meta?.user;
    if (metaUser != null) {
      store.dispatch(updateUserContext(metaUser));
    }
    return response;
  },
  async (error: AxiosError<ApiResponse>) => {
    if (isCanceledError(error)) {
      throw error;
    }
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
      skipGlobalErrorToast?: boolean;
    };

    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry
    ) {
      const isRefreshRequest =
        originalRequest.url?.includes("/auth/refresh") === true;
      if (isRefreshRequest) {
        invalidateLocationListCache();
        store.dispatch(clearUser());
        store.dispatch(resetLocationState());
        store.dispatch(setLocationListHydrated(false));
        // Don't toast: no refresh cookie is normal on login/set-password/forgot-password
        throw error;
      }

      originalRequest._retry = true;
      try {
        await doRefresh();
        return api(originalRequest);
      } catch {
        invalidateLocationListCache();
        store.dispatch(clearUser());
        store.dispatch(resetLocationState());
        store.dispatch(setLocationListHydrated(false));
        toast.error(getErrorMessage(error));
        throw error;
      }
    }

    if (!originalRequest?.skipGlobalErrorToast) {
      toast.error(getErrorMessage(error));
    }
    throw error;
  }
);

export default api;
